import { useEffect, useCallback, useState } from "react";
import { Route, Switch, useRouteMatch, useHistory, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { Formik, Form } from "formik";
import { useFlowConfig } from "flows/hooks";
import { IframeOrFullPageWrapper } from "components/Container";
import ScrollToTop from "components/ScrollToTop";
import Header from "flows/components/Header";
import PayoutAmount from "./components/PayoutAmount";
import PayoutDestination from "./components/PayoutDestination";
import { getFixedAmount } from "flows/PayoutV2/utils";
import { FraudCheck } from "components/FraudCheck";
import { BankAccountForm, MissingAddressForm } from "components/AddPaymentMethod";
import BankRailOptions from "./components/BankRailOptions";
import Confirmation from "./components/Confirmation/";
import {
  parsePlaidDetailsFromFlowState,
  parsePaypalDetailsFromFlowState,
  useSessionCtx,
  useSessionJWT,
  useSessionStorage,
  FLOW_STATE,
  useAnalytics,
} from "utils";
import { createPayoutRequest } from "api/invoicing";
import Success from "flows/PayoutV2/components/Success";
import { useSettings } from "utils/settingsContext";
import { logError } from "api/accounts-customer";

export const getInitialPayoutFormValues = ({
  fixedAmount,
  userDefinedPayoutInfo,
  setUserDefinedPayoutInfo,
  currency,
}) => {
  let amount = 0;
  let sourcePaymentMethodId = null;
  const fixed = fixedAmount && getFixedAmount(fixedAmount);
  if (userDefinedPayoutInfo) {
    // this is set when a user has already started the flow, then left the UI to handle PayPal or Plaid account add through some OAuth flow
    // when the user comes back, we don't want to require them to set the amount again
    try {
      const decoded = JSON.parse(userDefinedPayoutInfo);
      amount = decoded.amount || 0;
      sourcePaymentMethodId = decoded.sourcePaymentMethodId || null;
    } catch {
      /* If there's any issue we just won't pull in any previous values */
      amount = 0;
      sourcePaymentMethodId = null;
    }
    setUserDefinedPayoutInfo("");
  } else if (fixed) {
    amount = fixed;
  }
  const initValues = {
    amount,
    destinationPaymentMethodId: null,
    payoutDestination: null,
    payoutRails: null,
    sourcePaymentMethodId,
    currency: currency || "USD",
    payoutAmountDetails: null,
    bankRailsVendor: null,
  };
  return initValues;
};

const PayoutV2 = ({ onReady, onComplete }) => {
  // analytics
  const analytics = useAnalytics();
  // Internationalization
  const { formatMessage } = useIntl();
  // Initialize routing history and current route path
  const history = useHistory();
  const location = useLocation();
  const { path } = useRouteMatch();
  // txn limits
  const { integratorSettings } = useSettings();
  const payoutTransactionLimits = integratorSettings?.transaction_limits?.payout || {};
  // Flow configuration
  const { hideExitButtons, currency, fixedAmount } = useFlowConfig();
  const currencyToUse = (currency && currency.toUpperCase()) || "USD"; // Get currency or default to USD
  // flowState used to see if we have a new bank account added
  const {
    sessionCtx: { flowState },
    setFlowState,
    sessionId,
  } = useSessionCtx();
  const { sessionJWT } = useSessionJWT();
  const [userDefinedPayoutInfo, setUserDefinedPayoutInfo] = useSessionStorage(
    "tilia.ac.userDefinedPayoutInfo",
  );
  /**
   * storedPayoutFormValues
   * Used at first just to set the initial form values for payout.
   * Also used to store values if the user has to pop over to the manual
   * bank account form.
   */
  const [storedPayoutFormValues, setStoredPayoutFormValues] = useState(
    getInitialPayoutFormValues({
      fixedAmount,
      userDefinedPayoutInfo,
      setUserDefinedPayoutInfo,
      currency: currencyToUse
    }),
  );
  // State for bank account and banking payment method
  const [bankAccountId, setBankAccountId] = useState(null);

  const [isLoadingPayoutRequest, setIsLoadingPayoutRequest] = useState(false);
  const [getPayoutRequestError, setGetPayoutRequestError] = useState(null);
  const [payoutRequestResponse, setPayoutRequestResponse] = useState(null);

  useEffect(() => {
    // Trigger the onReady effect when the component mounts
    onReady && onReady({ state: FLOW_STATE.READY });
  }, [onReady]);

  // Function to navigate to add payment method screen
  const goToSelectPayoutDestionScreen = useCallback(
    ({ newlyAddedBankAccountId, newlyAddedPayPalId } = {}) => {
      let qStr = ""; // default
      if (newlyAddedBankAccountId) {
        qStr = `?newlyAddedBankAccountId=${newlyAddedBankAccountId}`;
      } else if (newlyAddedPayPalId) {
        qStr = `?newlyAddedPayPalId=${newlyAddedPayPalId}`;
      }
      history.push(`${path}/payout-destination${qStr}`);
    },
    [history, path],
  );

  // Function to navigate to success screen
  const goSuccessScreen = useCallback(() => {
    history.push(`${path}/success`);
  }, [history, path]);

  const goToMissingAddressForBankAccountForm = useCallback((bankOwnerId) => {
    history.push(`${path}/missing-address-for-bank-account/${bankOwnerId}`);
  }, [history, path]);

  /**
   * handlePossibleReturnFromAddingNewBankOrPaypal
   * If we returned from adding a bank account through Plaid or a paypal account
   * Let's take the user directly to the add payment method screen where we'll
   * load the user's bank accounts so they can create a Masspay payment method option.
   * Only run this on page load.
  */
  const handlePossibleReturnFromAddingNewBankOrPaypal = useCallback(() => {
    const { bankAccountId, bankOwnerId, state: plaidState } = parsePlaidDetailsFromFlowState(flowState);
    const { state, pmId: paypalId } = parsePaypalDetailsFromFlowState(flowState);

    if (bankOwnerId && plaidState === "pending") {
      setFlowState(); // clear flowState
      goToMissingAddressForBankAccountForm(bankOwnerId);
    } else if (bankAccountId && storedPayoutFormValues.amount && storedPayoutFormValues.sourcePaymentMethodId) {
      // if we don't have an amount or sourcePaymentMethodId for some reason, we don't want to bypass this section
      setFlowState(); // clear flowState after grabbing bankAccountId
      goToSelectPayoutDestionScreen({ newlyAddedBankAccountId: bankAccountId });
    } else if (paypalId && state === "complete" && storedPayoutFormValues.amount) {
      setFlowState(); // clear flowState after grabbing paypalId
      goToSelectPayoutDestionScreen({ newlyAddedPayPalId: paypalId });
    }
  }, [flowState, setFlowState, goToSelectPayoutDestionScreen, goToMissingAddressForBankAccountForm, storedPayoutFormValues])

  /**
   * useEffect to see if we returned from
   * adding a bank account through Plaid or a paypal account.
   */
  useEffect(() => {
    handlePossibleReturnFromAddingNewBankOrPaypal();
  }, [handlePossibleReturnFromAddingNewBankOrPaypal]);

  // Function to handle go back navigation event
  const handleGoBack = useCallback(async () => {
    if (getPayoutRequestError) {
      // if in the case of the confirmation page submit getting
      // an error and the user clicking the back button, we
      // want to reset the error.
      setGetPayoutRequestError(null);
    }
    history.goBack();
  }, [history, getPayoutRequestError]);

  // Function to handle close button click event
  const handleCloseButtonClicked = () => {
    const payload = {
      state: payoutRequestResponse?.payout_status_id ? FLOW_STATE.COMPLETE : FLOW_STATE.CANCEL,
      trigger: "header-close-button",
      payout_status_id: payoutRequestResponse?.payout_status_id || null,
    };
    onComplete(payload);
  };

  const handlePayoutAmountStageComplete = () => {
    goToSelectPayoutDestionScreen();
  };

  const goToConfirmationPage = useCallback(() => {
    history.push(`${path}/confirmation`);
  }, [history, path]);

  const goToPayoutAmountPage = useCallback(() => {
    history.push(`${path}/`);
  }, [history, path]);

  const handlePayoutDestinationSelected = (payoutDestination = {}) => {
    if ("bank_account_id" in payoutDestination) {
      goToBankRailOptionsScreen(payoutDestination.bank_account_id);
    } else {
      goToConfirmationPage();
    }
  };

  const handlePayoutRailsSelection = useCallback(() => {
    goToConfirmationPage();
  }, [goToConfirmationPage]);

  // Function to navigate to bank rail options screen
  const goToBankRailOptionsScreen = useCallback(
    (bankAccountId) => {
      setBankAccountId(bankAccountId);
      setTimeout(() => {
        history.push(`${path}/bank-rail-options`);
      }, 0);
    },
    [history, path],
  );

  const handleFormSubmit = async (values) => {
    setIsLoadingPayoutRequest(true);
    setGetPayoutRequestError(null);
    setPayoutRequestResponse(null);
    let isSuccessful = false;
    try {
      const ctx = {
        ...sessionJWT,
        sessionId,
      };
      const {
        sourcePaymentMethodId,
        destinationPaymentMethodId,
        payoutRails,
        amount,
        payoutDestination,
      } = values || {};
      const { id: payoutRailsID } = payoutRails || {};
      const submitPayload = {
        source_payment_method_id: sourcePaymentMethodId,
        destination_payment_method_id: destinationPaymentMethodId,
        amount: amount,
      };
      if ("bank_account_id" in payoutDestination) {
        submitPayload.bank_request = {
          provider: values.bankRailsVendor || "masspay",
          payout_service_id: payoutRailsID,
        };
      }
      const response = await createPayoutRequest(ctx, submitPayload);
      isSuccessful = true;
      setPayoutRequestResponse(response.payload);
      goSuccessScreen();
    } catch (err) {
      logError(sessionJWT, { message: err.message, flowLoc: "payoutv2.handleFormSubmit", data: err.data });
      setGetPayoutRequestError(err);
    }
    setIsLoadingPayoutRequest(false);
    analytics.track("PayoutV2_Payout_Request_Submit", {
      isSuccessful,
      isPayoutToBankAccount: !!(values?.payoutDestination?.bank_account_id),
      bankAccountCountry: values?.payoutDestination?.country_iso || null,
      bankRails: values?.payoutRails?.name || null,
      bankRailsCurrencyUnsupported: values?.payoutRails?.bank_currency_unsupported || null, // handles case if user selects USD option for a foreign currency bank
      payoutAmountDetails: values?.payoutAmountDetails || null,
      bankRailsVendor: values?.bankRailsVendor,
    });
  };

  const handleFormValidation = (values) => {
    let errors = {};
    if (!values.amount) errors.amount = "Required";
    if (!values.sourcePaymentMethodId) errors.sourcePaymentMethodId = "Required";
    if (!values.destinationPaymentMethodId) errors.destinationPaymentMethodId = "Required";
    if (
      values.payoutDestination &&
      "bank_account_id" in values.payoutDestination &&
      !values.payoutRails?.id
    ) {
      errors.payoutRails = "Required";
    }
    return errors;
  };

  return (
    <IframeOrFullPageWrapper includeTosAndPrivacy withoutPadding>
      <FraudCheck isAsync />
      <ScrollToTop>
        <Header
          showLanguage
          title={formatMessage({ defaultMessage: "Payout", description: "Page title" })}
          onBackButtonClick={
            // Conditionally handle go back navigation
            history.location.pathname === path || history.location.pathname.includes("success")
              ? null
              : handleGoBack
          }
          onCloseButtonClick={!hideExitButtons ? handleCloseButtonClicked : undefined} // Handle close button click event
        />
        <Switch>
          <Route
            exact
            path={`${path}/bank-account-details`}
            component={() => {
              const countryISO = location.state?.countryISO;
              return (
                <BankAccountForm
                  onSuccess={(bankAccountId) => goToSelectPayoutDestionScreen({ newlyAddedBankAccountId: bankAccountId })}
                  countryISO={countryISO}
                />
              );
            }}
          />
          <Route exact path={`${path}/missing-address-for-bank-account/:bankOwnerId`}>
            <MissingAddressForm
              onSuccess={goToPayoutAmountPage}
              countryISO="US"
            />
          </Route>
          <Route path={`${path}/`}>
            <Formik
              initialValues={storedPayoutFormValues}
              validate={handleFormValidation}
              onSubmit={handleFormSubmit}
            >
              <Form>
                <Switch>
                  <Route exact path={`${path}/`}>
                    <PayoutAmount
                      currencyToUse={currencyToUse}
                      onNextBtnClicked={handlePayoutAmountStageComplete}
                      txnLimits={payoutTransactionLimits}
                    />
                  </Route>
                  <Route path={`${path}/payout-destination`}>
                    <PayoutDestination
                      path={path}
                      onPayoutDestinationSelected={handlePayoutDestinationSelected}
                      onStoreFormValuesTemporarily={setStoredPayoutFormValues}
                    />
                  </Route>
                  <Route path={`${path}/bank-rail-options`}>
                    <BankRailOptions
                      bankAccountId={bankAccountId}
                      onPayoutRailsSelection={handlePayoutRailsSelection}
                    />
                  </Route>
                  <Route path={`${path}/confirmation`}>
                    <Confirmation
                      isSubmitting={isLoadingPayoutRequest}
                      submitError={getPayoutRequestError}
                    />
                  </Route>
                  <Route exact path={`${path}/success`}>
                    <Success
                      onComplete={handleCloseButtonClicked}
                      hideExitButtons={hideExitButtons}
                    />
                  </Route>
                </Switch>
              </Form>
            </Formik>
          </Route>
        </Switch>
      </ScrollToTop>
    </IframeOrFullPageWrapper>
  );
};

export default PayoutV2;